import React, { memo } from 'react';
import { Box, Chip, IconButton, Paper, Tooltip, Typography, alpha } from '@mui/material';
import { MoreHoriz, ArrowForwardRounded, EventOutlined } from '@mui/icons-material';
import { formatDistanceToNowStrict } from 'date-fns';
import { GeneratedQuestionPaper, PaperStatus } from '../../services/generateQuestionPaperService';
import { fmtClassName } from '../../utils/pdf/format';

interface PaperCardProps {
  paper: GeneratedQuestionPaper;
  canEditModule: boolean;
  canViewModule: boolean;
  onEdit: (paper: GeneratedQuestionPaper) => void;
  onView: (paper: GeneratedQuestionPaper) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, paper: GeneratedQuestionPaper) => void;
}

const STATUS_COLOR: Record<PaperStatus, 'warning' | 'success' | 'default'> = {
  [PaperStatus.DRAFT]: 'warning',
  [PaperStatus.PUBLISHED]: 'success',
  [PaperStatus.ARCHIVED]: 'default',
};

const nameOf = (ref: unknown): string => (ref && typeof ref === 'object' ? (ref as { name?: string }).name || '' : '');

/** A paper in the list. The whole card opens it: drafts for editing, the rest for viewing. */
const PaperCard = memo<PaperCardProps>(({
  paper,
  canEditModule,
  canViewModule,
  onEdit,
  onView,
  onMenuOpen,
}) => {
  const isDraft = paper.status === PaperStatus.DRAFT;
  // A draft opens for editing; anyone who can't edit can't open it.
  const open = isDraft ? (canEditModule ? () => onEdit(paper) : null) : () => onView(paper);
  const subject = nameOf(paper.subjectId);
  const className = fmtClassName(nameOf(paper.classId));
  const creator = nameOf(paper.createdBy) || (typeof paper.createdBy === 'object' ? paper.createdBy.email : '');
  const created = paper.createdAt
    ? `Created${creator ? ` by ${creator}` : ''} on ${new Date(paper.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : '';
  const examDate = paper.examDate
    ? new Date(paper.examDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null;

  return (
    <Paper
      variant="outlined"
      role={open ? 'button' : undefined}
      tabIndex={open ? 0 : undefined}
      onClick={open ?? undefined}
      onKeyDown={(e) => { if (open && (e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); open(); } }}
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '14px',
        p: 2,
        cursor: open ? 'pointer' : 'default',
        outline: 'none',
        transition: 'border-color 150ms, box-shadow 150ms, transform 150ms',
        '&:hover': open ? {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.07)}`,
          transform: 'translateY(-1px)',
        } : undefined,
        '&:hover .pc-open': { opacity: 1, transform: 'translateX(0)' },
        '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}` },
      })}
    >
      {/* Status and actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Chip
          label={paper.status}
          size="small"
          color={STATUS_COLOR[paper.status]}
          variant="outlined"
          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
        />
        {canViewModule && (
          <Tooltip title="More actions">
            <IconButton
              size="small"
              aria-label="More actions"
              onClick={(e) => { e.stopPropagation(); onMenuOpen(e, paper); }}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ mr: -0.75, color: 'text.secondary' }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Title and what it's for */}
      <Typography
        variant="subtitle1"
        dir="auto"
        sx={{
          fontWeight: 700,
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textAlign: 'start',
        }}
      >
        {paper.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
        {[subject, className].filter(Boolean).join(' · ')}
      </Typography>

      {/* The paper at a glance */}
      <Box sx={{ display: 'flex', gap: 2.5, mt: 2, mb: 2 }}>
        <Fact value={paper.totalMarks} label="marks" />
        <Fact value={paper.duration} label="min" />
        {examDate && (
          <Tooltip title="Exam date">
            <Fact icon={<EventOutlined sx={{ fontSize: 17, color: 'text.secondary' }} />} value={examDate} />
          </Tooltip>
        )}
      </Box>

      {/* Last worked on */}
      <Box
        sx={{
          position: 'relative',
          mt: 'auto',
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* A long name gives way; when it was edited always shows in full. */}
        <Tooltip title={created} placement="bottom-start">
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
            {creator && <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{creator}</Box>}
            {paper.updatedAt && (
              <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {creator && '\u00A0·\u00A0'}Edited {formatDistanceToNowStrict(new Date(paper.updatedAt), { addSuffix: true })}
              </Box>
            )}
          </Typography>
        </Tooltip>
        {open && (
          <Box
            className="pc-open"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              // Over the footer's end on hover, so it never takes room from the name.
              position: { md: 'absolute' },
              right: 0,
              bottom: 0,
              pl: 2,
              bgcolor: 'background.paper',
              color: 'primary.main',
              fontSize: '0.75rem',
              fontWeight: 600,
              opacity: { xs: 1, md: 0 },
              transform: { md: 'translateX(-4px)' },
              transition: 'opacity 150ms, transform 150ms',
            }}
          >
            {isDraft ? 'Edit' : 'View'}
            <ArrowForwardRounded sx={{ fontSize: 15 }} />
          </Box>
        )}
      </Box>
    </Paper>
  );
});

/** One figure on the card: a bold value with a quiet label, or an icon standing in for one. */
const Fact = React.forwardRef<HTMLDivElement, { value: React.ReactNode; label?: string; icon?: React.ReactNode }>(
  ({ value, label, icon, ...rest }, ref) => (
    <Box ref={ref} {...rest} sx={{ display: 'flex', alignItems: icon ? 'center' : 'baseline', gap: 0.5 }}>
      {icon}
      <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{value}</Typography>
      {label && <Typography component="span" variant="body2" color="text.secondary">{label}</Typography>}
    </Box>
  ),
);
Fact.displayName = 'Fact';

PaperCard.displayName = 'PaperCard';

export default PaperCard;
